import { useState, useEffect } from 'react';
import "../../styles/users_management/users-management.css";
import { useTranslation } from 'react-i18next';
import CreateUser from './CreateUser.jsx';
import UpdateUser from './UpdateUser.jsx';
import Loading from '../Loading.jsx';
import { useAuth } from '../context/AuthContext';
import Header from '../Header.jsx';
import { useNavigate, useParams } from 'react-router-dom';
import DashboardSidebar from '../DashboardSideBar.jsx';
import { fetchUploadedFiles } from '../documents/DocsManagement.jsx';
import ConfirmationModal from '../ConfirmationModal.jsx';
const tusApi = process.env.REACT_APP_TUS_SERVER;

export default function UsersManagement() {
    const [searchTerm, setSearchTerm] = useState('');
    const { t } = useTranslation();
    const [users, setUsers] = useState([]);
    const [selectedUser, setSelectedUser] = useState(null);
    const [showModal, setShowModal] = useState(false);
    const [modalMode, setModalMode] = useState(null);
    const [editingUser, setEditingUser] = useState(null);
    const apiUrl = process.env.REACT_APP_OPT_SERVER || "http://localhost:8888";
    const [uploadedFiles, setUploadedFiles] = useState([]);
    const [message, setMessage] = useState("");
    const { lang } = useParams();
    const navigate = useNavigate();
    const { user: currentUser, token, loading } = useAuth();
    const username = currentUser?.userName;
    const totalSizeBytes = uploadedFiles.reduce((sum, file) => sum + file.fileSize, 0);
    const totalSizeMB = totalSizeBytes / (1024 * 1024);
    const maxSize = 10;
    const [loadingData, setLoadingData] = useState(false);


    const [summary, setSummary] = useState({
        totalUsers: 0,
        totalFiles: 0,
        totalSize: "0 B"
    });
    const handleDetails = (user) => {
        const isSelf = currentUser?.userName && user?.username
            ? currentUser.userName === user.username
            : false;

        navigate(`/lang/${lang}/files/${user.username}`, {
            state: { user, isSelf },
        });
    };



    useEffect(() => {
        if (!username || !token) return;

        const fetchAllData = async () => {
            setLoadingData(true);
            try {
                // Fetch uploaded files
                const files = await fetchUploadedFiles(tusApi, username, token);
                setUploadedFiles(files);
                const usersRes = await fetch(
                    `${tusApi}/api/uploads/admin-dashboard?userId=${encodeURIComponent(username)}`,
                    {
                        method: "GET",
                        headers: {
                            Authorization: `Bearer ${token}`,
                            "Content-Type": "application/json",
                        },
                    }
                );

                if (!usersRes.ok) {
                    const errorData = await usersRes.json().catch(() => ({}));
                    throw new Error(
                        errorData.error ||
                        errorData.message ||
                        `Request failed with status ${usersRes.status}`
                    );
                }

                const { users: fetchedUsers = [], stats = [] } = await usersRes.json();

                const mergedUsers = fetchedUsers.map(user => {
                    const stat = stats.find(s => s.username === user.username) || {};
                    return {
                        ...user,
                        fileCount: stat.fileCount || 0,
                        totalSize: stat.totalSize || 0,
                        totalSizeMB: stat.totalSizeMB || "0.00",
                    };
                });

                setUsers(mergedUsers);

                const formatFileSize = (bytes) => {
                    if (!bytes) return "0 B";
                    if (bytes < 1024) return `${bytes} B`;
                    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
                    if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
                    return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`;
                };

                const totalUsers = mergedUsers.length;
                const totalFiles = mergedUsers.reduce((sum, user) => sum + user.fileCount, 0);
                const totalSizeBytes = mergedUsers.reduce((sum, user) => sum + user.totalSize, 0);
                const totalSize = formatFileSize(totalSizeBytes);

                setSummary({ totalUsers, totalFiles, totalSize });
            } catch (err) {
                console.error("Failed to load data:", err.message || err);
            } finally {
                setLoadingData(false);
            }
        };

        fetchAllData();
    }, [username, token, apiUrl]);

    const filteredUsers = (users ?? []).filter(user =>
        user.username.toLowerCase().includes(searchTerm.toLowerCase())
    );

    // Create new user
    const handleCreateUser = (user) => {
        setUsers([...users, user]);
    };
    const handleUpdateUser = (updatedUser) => {
        setUsers((prevUsers) =>
            prevUsers.map((u) => (u.userId === updatedUser.userId ? updatedUser : u))
        );
    };


    const openCreateModal = () => {
        setEditingUser(null);
        setModalMode("create");
        setShowModal(true);
    };

    const openEditModal = (user) => {
        setEditingUser(user);
        setModalMode("edit");
        setShowModal(true);
    };
    const ModalWrapper = ({ children }) => (
        <div className="modal-overlay-user" onClick={closeModal}>
            <div className="modal-content-user" onClick={(e) => e.stopPropagation()}>
                <button className="modal-close-button-user" onClick={closeModal}>
                    <svg xmlns="http://www.w3.org/2000/svg" width="30" height="30" viewBox="0 0 24 24"><path fill="currentColor" fill-rule="evenodd" d="M6.793 6.793a1 1 0 0 1 1.414 0L12 10.586l3.793-3.793a1 1 0 1 1 1.414 1.414L13.414 12l3.793 3.793a1 1 0 0 1-1.414 1.414L12 13.414l-3.793 3.793a1 1 0 0 1-1.414-1.414L10.586 12L6.793 8.207a1 1 0 0 1 0-1.414" clip-rule="evenodd" /></svg>
                </button>
                {children}
            </div>
        </div>
    );
    // Close modal
    const closeModal = () => {
        setShowModal(false);
        setSelectedUser(null);
        setModalMode(null);
    };

    const openDeleteModal = (user) => {
        console.log("Opening delete modal with user:", user); // 👈 should log full object
        setSelectedUser(user); // ✅ store entire object, not just ID
        setModalMode("delete");
        setShowModal(true);
    };

    // -----------------------
    // 3. Confirm Delete
    // -----------------------
    const handleConfirmDelete = async () => {
        if (!selectedUser) {
            console.error("No user selected for deletion.");
            setMessage("Error: No user selected.");
            setTimeout(() => setMessage(""), 3000);
            return;
        }

        if (!selectedUser._id) {
            console.error("Selected user has no _id:", selectedUser);
            setMessage("Error: Selected user is missing an ID.");
            setTimeout(() => setMessage(""), 3000);
            return;
        }

        try {
            const response = await fetch(`${apiUrl}/api/users/${selectedUser._id}`, {
                method: "DELETE",
                headers: {
                    Authorization: `Bearer ${token}`,
                    "Content-Type": "application/json",
                },
            });

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                throw new Error(errorData.message || `Failed with status ${response.status}`);
            }


            setUsers((prev) => prev.filter((u) => u._id !== selectedUser._id));


            setMessage(` User ${selectedUser.username} deleted successfully.`);
            setTimeout(() => setMessage(""), 3000);
        } catch (error) {
            console.error("Delete failed:", error);
            setMessage(` Failed to delete user: ${error.message}`);
            setTimeout(() => setMessage(""), 5000); // show longer for errors
        } finally {
            setSelectedUser(null);
            setShowModal(false);
        }
    };


    if (loading || loadingData) return <Loading />;

    return (
        <div className="uploadpage">
            <Header hideHamburger={false} profileHide={true} hideNav={true} />
            <div className="dashboard">
                <DashboardSidebar
                    uploadedFiles={uploadedFiles}
                    totalSizeMB={totalSizeMB}
                    maxSize={maxSize}
                />

                <div className="user_manager">
                    <div className="container">
                        <div className="head">
                            <h2>{t('user')}</h2>
                            <p>{t('user-management-description')}</p>

                        </div>
                        <div className="contents">
                            <div className="content">
                                <div className="searches">
                                    <div className="search">
                                        <input
                                            type="text"
                                            placeholder={t('search-user')}
                                            value={searchTerm}
                                            onChange={(e) => setSearchTerm(e.target.value)}
                                        />
                                        <button className="search-button">
                                            <svg xmlns="http://www.w3.org/2000/svg" height="24px" viewBox="0 -960 960 960" width="24px" fill="#9ca3af">
                                                <path d="M380-320q-109 0-184.5-75.5T120-580q0-109 75.5-184.5T380-840q109 0 184.5 75.5T640-580q0 44-14 83t-38 69l224 224q11 11 11 28t-11 28q-11 11-28 11t-28-11L532-372q-30 24-69 38t-83 14Zm0-80q75 0 127.5-52.5T560-580q0-75-52.5-127.5T380-760q-75 0-127.5 52.5T200-580q0 75 52.5 127.5T380-400Z" />
                                            </svg>
                                        </button>
                                    </div>
                                    <button className="add-new" onClick={openCreateModal} >
                                        <svg xmlns="http://www.w3.org/2000/svg" height="24px" viewBox="0 -960 960 960" width="24px" fill="#e3e3e3"><path d="M440-440H240q-17 0-28.5-11.5T200-480q0-17 11.5-28.5T240-520h200v-200q0-17 11.5-28.5T480-760q17 0 28.5 11.5T520-720v200h200q17 0 28.5 11.5T760-480q0 17-11.5 28.5T720-440H520v200q0 17-11.5 28.5T480-200q-17 0-28.5-11.5T440-240v-200Z" /></svg>
                                        {t('add-new-user')}
                                    </button>

                                </div>
                                <div className="user-table-wrapper">
                                    <table className="user-table">
                                        <thead>
                                            <tr>
                                                <th>
                                                    <div className="username">
                                                        <input
                                                            type="checkbox"
                                                        />
                                                        <div className="head-user">
                                                            <span>{t('account name')}</span>
                                                            <svg
                                                                xmlns="http://www.w3.org/2000/svg"
                                                                height="20"
                                                                viewBox="0 -960 960 960"
                                                                width="20"
                                                                fill="#9ca3af"
                                                            >
                                                                <path d="M360-440q-17 0-28.5-11.5T320-480v-247l-75 75q-11 11-27.5 11T189-652q-12-12-12-28.5t12-28.5l143-143q6-6 13-8.5t15-2.5q8 0 15 2.5t13 8.5l144 144q12 12 11.5 28T531-652q-12 11-28 11.5T475-652l-75-75v247q0 17-11.5 28.5T360-440ZM600-97q-8 0-15-2.5t-13-8.5L428-252q-12-12-11.5-28t12.5-28q12-11 28-11.5t28 11.5l75 75v-247q0-17 11.5-28.5T600-520q17 0 28.5 11.5T640-480v247l75-75q11-11 27.5-11t28.5 11q12 12 12 28.5T771-251L628-108q-6 6-13 8.5T600-97Z" />
                                                            </svg>
                                                        </div>

                                                    </div>
                                                </th>
                                                <th>
                                                    <div className="username">
                                                        <div className="head-user">
                                                            <span>{t('total-file')}</span>
                                                            <svg
                                                                xmlns="http://www.w3.org/2000/svg"
                                                                height="20"
                                                                viewBox="0 -960 960 960"
                                                                width="20"
                                                                fill="#9ca3af"
                                                            >
                                                                <path d="M360-440q-17 0-28.5-11.5T320-480v-247l-75 75q-11 11-27.5 11T189-652q-12-12-12-28.5t12-28.5l143-143q6-6 13-8.5t15-2.5q8 0 15 2.5t13 8.5l144 144q12 12 11.5 28T531-652q-12 11-28 11.5T475-652l-75-75v247q0 17-11.5 28.5T360-440ZM600-97q-8 0-15-2.5t-13-8.5L428-252q-12-12-11.5-28t12.5-28q12-11 28-11.5t28 11.5l75 75v-247q0-17 11.5-28.5T600-520q17 0 28.5 11.5T640-480v247l75-75q11-11 27.5-11t28.5 11q12 12 12 28.5T771-251L628-108q-6 6-13 8.5T600-97Z" />
                                                            </svg>
                                                        </div>

                                                    </div>
                                                </th>
                                                <th>
                                                    <div className="username">
                                                        <div className="head-user">
                                                            <span>{t('total-size')}</span>
                                                            <svg
                                                                xmlns="http://www.w3.org/2000/svg"
                                                                height="20"
                                                                viewBox="0 -960 960 960"
                                                                width="20"
                                                                fill="#9ca3af"
                                                            >
                                                                <path d="M360-440q-17 0-28.5-11.5T320-480v-247l-75 75q-11 11-27.5 11T189-652q-12-12-12-28.5t12-28.5l143-143q6-6 13-8.5t15-2.5q8 0 15 2.5t13 8.5l144 144q12 12 11.5 28T531-652q-12 11-28 11.5T475-652l-75-75v247q0 17-11.5 28.5T360-440ZM600-97q-8 0-15-2.5t-13-8.5L428-252q-12-12-11.5-28t12.5-28q12-11 28-11.5t28 11.5l75 75v-247q0-17 11.5-28.5T600-520q17 0 28.5 11.5T640-480v247l75-75q11-11 27.5-11t28.5 11q12 12 12 28.5T771-251L628-108q-6 6-13 8.5T600-97Z" />
                                                            </svg>
                                                        </div>

                                                    </div>
                                                </th>
                                                <th>
                                                    <div className="username">
                                                        <div className="head-user">
                                                            <span>{t('entity')}</span>
                                                            <svg
                                                                xmlns="http://www.w3.org/2000/svg"
                                                                height="20"
                                                                viewBox="0 -960 960 960"
                                                                width="20"
                                                                fill="#9ca3af"
                                                            >
                                                                <path d="M360-440q-17 0-28.5-11.5T320-480v-247l-75 75q-11 11-27.5 11T189-652q-12-12-12-28.5t12-28.5l143-143q6-6 13-8.5t15-2.5q8 0 15 2.5t13 8.5l144 144q12 12 11.5 28T531-652q-12 11-28 11.5T475-652l-75-75v247q0 17-11.5 28.5T360-440ZM600-97q-8 0-15-2.5t-13-8.5L428-252q-12-12-11.5-28t12.5-28q12-11 28-11.5t28 11.5l75 75v-247q0-17 11.5-28.5T600-520q17 0 28.5 11.5T640-480v247l75-75q11-11 27.5-11t28.5 11q12 12 12 28.5T771-251L628-108q-6 6-13 8.5T600-97Z" />
                                                            </svg>
                                                        </div>

                                                    </div>
                                                </th>
                                                <th>
                                                    <div className="username">
                                                        <div className="head-user">
                                                            <span>{t('last-log-in')}</span>
                                                            <svg
                                                                xmlns="http://www.w3.org/2000/svg"
                                                                height="20"
                                                                viewBox="0 -960 960 960"
                                                                width="20"
                                                                fill="#9ca3af"
                                                            >
                                                                <path d="M360-440q-17 0-28.5-11.5T320-480v-247l-75 75q-11 11-27.5 11T189-652q-12-12-12-28.5t12-28.5l143-143q6-6 13-8.5t15-2.5q8 0 15 2.5t13 8.5l144 144q12 12 11.5 28T531-652q-12 11-28 11.5T475-652l-75-75v247q0 17-11.5 28.5T360-440ZM600-97q-8 0-15-2.5t-13-8.5L428-252q-12-12-11.5-28t12.5-28q12-11 28-11.5t28 11.5l75 75v-247q0-17 11.5-28.5T600-520q17 0 28.5 11.5T640-480v247l75-75q11-11 27.5-11t28.5 11q12 12 12 28.5T771-251L628-108q-6 6-13 8.5T600-97Z" />
                                                            </svg>
                                                        </div>

                                                    </div>
                                                </th>
                                                <th>
                                                    <div className="username">
                                                        <div className="head-user">
                                                            <span>{t('role')}</span>
                                                            <svg
                                                                xmlns="http://www.w3.org/2000/svg"
                                                                height="20"
                                                                viewBox="0 -960 960 960"
                                                                width="20"
                                                                fill="#9ca3af"
                                                            >
                                                                <path d="M360-440q-17 0-28.5-11.5T320-480v-247l-75 75q-11 11-27.5 11T189-652q-12-12-12-28.5t12-28.5l143-143q6-6 13-8.5t15-2.5q8 0 15 2.5t13 8.5l144 144q12 12 11.5 28T531-652q-12 11-28 11.5T475-652l-75-75v247q0 17-11.5 28.5T360-440ZM600-97q-8 0-15-2.5t-13-8.5L428-252q-12-12-11.5-28t12.5-28q12-11 28-11.5t28 11.5l75 75v-247q0-17 11.5-28.5T600-520q17 0 28.5 11.5T640-480v247l75-75q11-11 27.5-11t28.5 11q12 12 12 28.5T771-251L628-108q-6 6-13 8.5T600-97Z" />
                                                            </svg>
                                                        </div>
                                                    </div>
                                                </th>
                                                <th>{t('action')}</th>
                                            </tr>

                                        </thead>
                                        <tbody>
                                            {filteredUsers.map((user) => (
                                                <tr key={user._id}>
                                                    <td>

                                                        <div className="username">
                                                            <input
                                                                type="checkbox"
                                                            />
                                                            <span
                                                                onClick={() =>
                                                                    handleDetails(user)
                                                                }

                                                                style={{ cursor: "pointer" }}
                                                            >
                                                                {user.username}
                                                            </span>
                                                        </div>
                                                    </td>
                                                    <td>{user.fileCount || 0} files</td>
                                                    <td>{user.totalSizeMB ? Number(user.totalSizeMB).toFixed(2) : "0.00"} MB</td>
                                                    <td>{user.entity || 'N/A'}</td>
                                                    <td>{user.lastLogin ? new Date(user.lastLogin).toLocaleString() : 'N/A'}</td>
                                                    <td>{user.role ?? 'N/A'}</td>
                                                    <td className="actions">
                                                        <button className="view-button" title="View" onClick={() => handleDetails(user.username)}>
                                                            <svg xmlns="http://www.w3.org/2000/svg" width="20px" height="20px" viewBox="0 0 32 32"><path fill="currentColor" d="M30.94 15.66A16.69 16.69 0 0 0 16 5A16.69 16.69 0 0 0 1.06 15.66a1 1 0 0 0 0 .68A16.69 16.69 0 0 0 16 27a16.69 16.69 0 0 0 14.94-10.66a1 1 0 0 0 0-.68M16 25c-5.3 0-10.9-3.93-12.93-9C5.1 10.93 10.7 7 16 7s10.9 3.93 12.93 9C26.9 21.07 21.3 25 16 25" /><path fill="currentColor" d="M16 10a6 6 0 1 0 6 6a6 6 0 0 0-6-6m0 10a4 4 0 1 1 4-4a4 4 0 0 1-4 4" /></svg>
                                                        </button>
                                                        <button className="edit-button" title="Edit" onClick={() => openEditModal(user)}>
                                                            <svg xmlns="http://www.w3.org/2000/svg" width="20px" height="20px" viewBox="0 0 24 24"><path fill="currentColor" d="M10.56 11.87a3.75 3.75 0 1 1 3.75-3.75a3.76 3.76 0 0 1-3.75 3.75m0-6a2.25 2.25 0 1 0 2.25 2.25a2.25 2.25 0 0 0-2.25-2.25m-7 13a.75.75 0 0 1-.75-.75c0-4.75 5.43-4.75 7.75-4.75c.72 0 1.36 0 1.94.07a.75.75 0 0 1 .69.8a.76.76 0 0 1-.81.69c-.54 0-1.14-.06-1.82-.06c-5.18 0-6.25 1.3-6.25 3.25a.74.74 0 0 1-.75.75m9.11.76a.75.75 0 0 1-.53-.22a.72.72 0 0 1-.22-.59l.16-1.92a.75.75 0 0 1 .21-.47l5.52-5.52a2.06 2.06 0 0 1 2.8 0a2 2 0 0 1 .58 1.44a1.86 1.86 0 0 1-.53 1.33l-5.52 5.52a.74.74 0 0 1-.46.22l-1.94.18Zm.88-2.34l-.06.76l.78-.07l5.33-5.33a.4.4 0 0 0 .09-.27a.6.6 0 0 0-.14-.38a.57.57 0 0 0-.68 0Z" /></svg>
                                                        </button>

                                                        <button onClick={() => openDeleteModal(user)} className="delete-button" title="Delete" >
                                                            <svg xmlns="http://www.w3.org/2000/svg" width="20px" height="20px" viewBox="0 0 24 24"><path fill="currentColor" d="M7.616 20q-.672 0-1.144-.472T6 18.385V6H5V5h4v-.77h6V5h4v1h-1v12.385q0 .69-.462 1.153T16.384 20zM17 6H7v12.385q0 .269.173.442t.443.173h8.769q.23 0 .423-.192t.192-.424zM9.808 17h1V8h-1zm3.384 0h1V8h-1zM7 6v13z" /></svg>
                                                        </button>

                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>

                                    </table>

                                </div>



                                {(() => {
                                    switch (modalMode) {
                                        case "edit":
                                            return <ModalWrapper><UpdateUser user={editingUser} onUpdate={handleUpdateUser} /></ModalWrapper>;
                                        case "create":
                                            return <ModalWrapper><CreateUser onCreate={handleCreateUser} onCancel={closeModal} /></ModalWrapper>;
                                        case "delete":
                                            return <ConfirmationModal
                                                isOpen={showModal}
                                                onClose={closeModal}
                                                onConfirm={handleConfirmDelete}
                                                header={
                                                    selectedUser
                                                        ? `${t("head-del-user")} ${selectedUser.username}?`
                                                        : ""
                                                }
                                                message={`${t('paragraph-del-user')}`
                                                }
                                            />;
                                        default:
                                            return (<div className="all-totally">
                                                <div className="total">
                                                    <h4>{t('total-file')} {t('colon')}</h4>
                                                    <span>{summary.totalFiles} {t('files')}</span>
                                                </div>
                                                <div className="total">
                                                    <h4>{t('total-size')} {t('colon')}</h4>
                                                    <span>{summary.totalSize}</span>
                                                </div>
                                                <div className="total">
                                                    <h4>{t('total-user')} {t('colon')}</h4>
                                                    <span>{summary.totalUsers} ​{t('users')}</span>
                                                </div>
                                            </div>)
                                    }
                                })()}

                            </div>
                        </div>
                    </div>

                    {message && (
                        <div
                            className="main-alert-box success"
                            role="alert"
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" width="20px" height="20px" viewBox="0 0 24 24"><path fill="currentColor" fill-rule="evenodd" d="M12 21a9 9 0 0 0 7.51-13.961l-7.155 7.95a2 2 0 0 1-2.687.262L6.4 12.8a1 1 0 0 1 1.2-1.6l3.268 2.451l7.346-8.161A9 9 0 1 0 12 21" clip-rule="evenodd" /></svg>
                            <span>{message}</span>
                            <button onClick={() => setMessage("")}>&times;</button>
                        </div>
                    )}

                </div>
            </div ></div >

    )
}
